import { PageContainer } from "@/components/layout/PageContainer";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { ArrowRight, Activity, Clock, Users, Shield } from "lucide-react";

const ClinicalAutomation = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>Clinical Automation Solutions - Scienza Health</title>
        <meta name="description" content="AI-powered clinical automation solutions that streamline workflows, reduce documentation burden, and enhance patient care quality." />
      </Helmet>
      
      <div className="min-h-screen mobile-section" style={{ background: 'var(--gradient-depth)' }}>
        <div className="mobile-container">
          <div className="text-center mobile-hero">
            <h1 className="mobile-text-3xl sm:text-4xl md:text-6xl font-bold mb-6 text-white">
              Clinical Automation
            </h1>
            <p className="mobile-text-lg sm:text-xl text-white/80 max-w-3xl mx-auto">
              Transform clinical workflows with intelligent automation that reduces administrative burden while improving patient care quality and safety.
            </p>
          </div>

          {/* Key Benefits */}
          <div className="mobile-grid mobile-grid-4 gap-4 sm:gap-6 mb-12 sm:mb-16">
            <div className="text-center mobile-card">
              <div className="bg-primary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4 touch-target">
                <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <h3 className="mobile-text-base sm:text-lg font-semibold mb-2 text-white">60-80% Time Savings</h3>
              <p className="mobile-text-sm text-white/70">Reduce documentation time dramatically</p>
            </div>
            <div className="text-center mobile-card">
              <div className="bg-primary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4 touch-target">
                <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <h3 className="mobile-text-base sm:text-lg font-semibold mb-2 text-white">Real-time Monitoring</h3>
              <p className="mobile-text-sm text-white/70">Continuous patient observation</p>
            </div>
            <div className="text-center mobile-card">
              <div className="bg-primary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4 touch-target">
                <Users className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <h3 className="mobile-text-base sm:text-lg font-semibold mb-2 text-white">Enhanced Care</h3>
              <p className="mobile-text-sm text-white/70">More time for patient interaction</p>
            </div>
            <div className="text-center mobile-card">
              <div className="bg-primary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4 touch-target">
                <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <h3 className="mobile-text-base sm:text-lg font-semibold mb-2 text-white">Improved Safety</h3>
              <p className="mobile-text-sm text-white/70">Early detection and alerts</p>
            </div>
          </div>

          {/* Solutions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16">
            <Link to="/solutions/clinical-automation/virtual-nursing-observation" className="group touch-target">
              <div className="mobile-card border hover:shadow-lg transition-all duration-300 h-full" style={{ background: 'var(--gradient-warm)' }}>
                <h3 className="mobile-text-lg sm:text-xl md:text-2xl font-semibold mb-4 group-hover:text-primary transition-colors text-white">
                  Virtual Nursing Observation
                </h3>
                <p className="mobile-text-sm sm:text-base text-white/80 mb-6">
                  AI-powered continuous patient monitoring that provides 24/7 observation capabilities, 
                  reducing the need for one-on-one sitters while maintaining patient safety and care quality.
                </p>
                <div className="flex items-center text-primary touch-target">
                  <span className="mobile-text-sm sm:text-base font-medium">Learn More</span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>

            <Link to="/solutions/clinical-automation/cognitive-screening-intake" className="group touch-target">
              <div className="mobile-card border hover:shadow-lg transition-all duration-300 h-full" style={{ background: 'var(--gradient-earth)' }}>
                <h3 className="mobile-text-lg sm:text-xl md:text-2xl font-semibold mb-4 group-hover:text-primary transition-colors text-white">
                  Cognitive Screening & Intake
                </h3>
                <p className="mobile-text-sm sm:text-base text-white/80 mb-6">
                  Automated cognitive assessment during patient intake that screens for Alzheimer's, MCI, 
                  depression, and PTSD through natural conversation and speech pattern analysis.
                </p>
                <div className="flex items-center text-primary touch-target">
                  <span className="mobile-text-sm sm:text-base font-medium">Learn More</span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>

            <Link to="/solutions/clinical-automation/remote-patient-monitoring" className="group touch-target">
              <div className="mobile-card border hover:shadow-lg transition-all duration-300 h-full" style={{ background: 'var(--gradient-warm)' }}>
                <h3 className="mobile-text-lg sm:text-xl md:text-2xl font-semibold mb-4 group-hover:text-primary transition-colors text-white">
                  Remote Patient Monitoring
                </h3>
                <p className="mobile-text-sm sm:text-base text-white/80 mb-6">
                  Comprehensive remote monitoring solution that tracks patient health metrics, 
                  medication adherence, and wellness indicators with intelligent alerts and interventions.
                </p>
                <div className="flex items-center text-primary touch-target">
                  <span className="mobile-text-sm sm:text-base font-medium">Learn More</span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>

            <Link to="/solutions/clinical-automation/patient-access-automation" className="group touch-target">
              <div className="mobile-card border hover:shadow-lg transition-all duration-300 h-full" style={{ background: 'var(--gradient-earth)' }}>
                <h3 className="mobile-text-lg sm:text-xl md:text-2xl font-semibold mb-4 group-hover:text-primary transition-colors text-white">
                  Patient Access Automation
                </h3>
                <p className="mobile-text-sm sm:text-base text-white/80 mb-6">
                  Streamlined patient access processes including scheduling, registration, insurance verification, 
                  and pre-visit preparation through intelligent automation.
                </p>
                <div className="flex items-center text-primary touch-target">
                  <span className="mobile-text-sm sm:text-base font-medium">Learn More</span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          </div>

          {/* CTA Section */}
          <div className="text-center rounded-lg mobile-px-safe py-6 sm:p-8" style={{ background: 'var(--gradient-warm)' }}>
            <h2 className="mobile-text-xl sm:text-2xl md:text-3xl font-bold mb-4 text-white">Ready to Automate Your Clinical Workflows?</h2>
            <p className="mobile-text-base sm:text-lg text-white/80 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Discover how our clinical automation solutions can reduce administrative burden and improve patient outcomes in your organization.
            </p>
            <Link 
              to="/contact-request-demo" 
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary font-medium rounded-lg hover:bg-white/90 transition-colors"
            >
              Book a Demo 
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default ClinicalAutomation;